import type { CurrentUser } from '@/utils/authSession';
import {
  canAccessPage,
  canOperateAction,
  hasAnyPermission,
  hasPermission,
  PERMISSION_CODES,
  resolvePermissionSnapshot,
} from '@/utils/mockPermissions';
import {
  canAccessAuthorizedEntryPath,
  canAccessRoutePath,
  findAuthorizedRedirectTarget,
} from '@/utils/routePermissions';

export interface AccessRoute {
  path?: string;
}

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
  | {
    currentUser?: CurrentUser;
    activityProcessEntry?: { ownerId: string; visible: boolean };
  }
  | undefined,
) {
  const { currentUser } = initialState ?? {};
  const processEntryVisible = Boolean(
    currentUser &&
      initialState?.activityProcessEntry?.ownerId === currentUser.id &&
      initialState?.activityProcessEntry?.visible,
  );
  const scopedProcessPath = (path?: string) =>
    processEntryVisible &&
    (path === '/' ||
      path === '/activity-center' ||
      path === '/activity-center/activity-workflow');
  const permissionSnapshot = resolvePermissionSnapshot();
  const canAccessAiConfiguration =
    canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.aiManagementConfigurationPage,
    ) &&
    hasPermission(permissionSnapshot, PERMISSION_CODES.aiConfigurationRead);
  const canAccessAiStatistics =
    canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.aiManagementStatisticsPage,
    ) && hasPermission(permissionSnapshot, PERMISSION_CODES.aiStatisticsRead);
  const canAccessNotificationChannelManagement = hasPermission(
    permissionSnapshot,
    [
      PERMISSION_CODES.notificationChannelManagementPage,
      PERMISSION_CODES.notificationChannelRead,
    ],
  );
  const canAccessNotificationTemplates = canAccessPage(
    permissionSnapshot,
    PERMISSION_CODES.notificationTemplatesPage,
  );
  const canAccessNotificationSend = canAccessPage(
    permissionSnapshot,
    PERMISSION_CODES.notificationSendPage,
  );
  const canAccessNotificationTriggerStrategies = canAccessPage(
    permissionSnapshot,
    PERMISSION_CODES.notificationTriggerStrategiesPage,
  );
  const canAccessNotificationSendRecords = canAccessPage(
    permissionSnapshot,
    PERMISSION_CODES.notificationSendRecordsPage,
  );

  return {
    canAccessActivityStaff: canAccessRoutePath(
      permissionSnapshot.permissionCodes,
      '/activity-center/staff-authorization',
    ),
    canAccessActivityAi: hasPermission(permissionSnapshot, [
      PERMISSION_CODES.activityAiPage,
      PERMISSION_CODES.activityAiRead,
    ]),
    canAccessActivityLottery: hasPermission(permissionSnapshot, [
      PERMISSION_CODES.activityLotteryPage,
      PERMISSION_CODES.activityLotteryGet,
    ]),
    canAccessLabelPrinting: canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.labelPrintingPage,
    ),
    canAccessActivityProcess:
      processEntryVisible ||
      canAccessPage(permissionSnapshot, PERMISSION_CODES.activityWorkflowPage),
    canAdmin: Boolean(currentUser && currentUser.access === 'admin'),
    canAccessOrganizationSettings: canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.organizationSettingsPage,
    ),
    canAccessUserPermissions: canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.userPermissionsPage,
    ),
    canAccessUserManagement: canAccessPage(
      permissionSnapshot,
      PERMISSION_CODES.userManagementPage,
    ),
    canAccessAiConfiguration,
    canAccessAiStatistics,
    canAccessAiManagement:
      hasPermission(permissionSnapshot, PERMISSION_CODES.aiManagementMenu) &&
      (canAccessAiConfiguration || canAccessAiStatistics),
    canAccessNotificationChannelManagement,
    canAccessNotificationTemplates,
    canAccessNotificationSend,
    canAccessNotificationTriggerStrategies,
    canAccessNotificationSendRecords,
    canAccessNotificationCenter:
      canAccessRoutePath(
        permissionSnapshot.permissionCodes,
        '/notification-center',
      ) &&
      canAccessAuthorizedEntryPath(
        permissionSnapshot.permissionCodes,
        '/notification-center',
      ),
    canAccessRoute: (route: AccessRoute) =>
      scopedProcessPath(route.path) ||
      canAccessRoutePath(permissionSnapshot.permissionCodes, route.path),
    canAccessAuthorizedEntry: (route: AccessRoute) =>
      scopedProcessPath(route.path) ||
      canAccessAuthorizedEntryPath(
        permissionSnapshot.permissionCodes,
        route.path,
      ),
    getAuthorizedRedirectTarget: (entryPath?: string) =>
      findAuthorizedRedirectTarget(
        permissionSnapshot.permissionCodes,
        entryPath,
      ) ||
      (processEntryVisible &&
      (entryPath === '/activity-center' || entryPath === '/')
        ? '/activity-center/activity-workflow'
        : undefined),
    hasPermission: (permissionCodes: string | string[]) =>
      hasPermission(permissionSnapshot, permissionCodes),
    hasAnyPermission: (permissionCodes: string[]) =>
      hasAnyPermission(permissionSnapshot, permissionCodes),
    canOperate: (actionCode: string) =>
      canOperateAction(permissionSnapshot, actionCode),
  };
}
